// External Packages
import { useState, useEffect, useContext, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { MdCheck, MdClose, MdEdit, MdRefresh } from "react-icons/md";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { toTitleCase } from "../../util/format-text";

// Internal Components
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import { requestAdminAPI } from "../../api/posh-api";
import LinkPair from "../UI/LinkPair";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import ImageSelector from "../UI/ImageSelector";
import SelectPair from "../UI/SelectPair";
import AuditLogs from "../UI/AuditLogs";

export default function LocationInfo() {
  // Load Context
  const { fetchLocations } = useContext(DataContext);

  // Get Location ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [location, setLocation] = useState({});
  const [edit, setEdit] = useState(false);

  /* ======================= FETCH LOCATION ======================= */
  const fetchLocation = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "location",
        id,
      });
      setLocation(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= EDIT LOCATION ======================= */
  const editHandler = async () => {
    try {
      setLoading(true);
      const image = document.getElementById("image").files[0];
      const tax = document.getElementById("tax").value;
      const payload = {
        locationId: location.id,
        phone: document.getElementById("phone").value,
        tax,
        active: document.getElementById("active").value === "Yes",
        deliveryRate: document.getElementById("deliveryRate").value,
        googleMapsLink: document.getElementById("googleMapsLink").value,
        googleIframe: document.getElementById("googleIframe").value,
        appleMapsLink: document.getElementById("appleMapsLink").value,
      };

      if (image) {
        const imageUrl = image
          ? await uploadImageToFirebase(
              `locations/${id}`,
              id,
              image,
              setUploading,
            )
          : "";
        payload.image = imageUrl;
      }

      // Upload Changes
      await requestAdminAPI("edit-location", payload);

      fetchLocations(() => {
        setEdit(false);
        fetchLocation();
      });
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  // Fetch location on load
  useEffect(() => {
    fetchLocation();
  }, []);

  if (!id || (!loading && !location))
    return (
      <div className="container">
        <p className="text-stone-400">
          Location ID Not Provided or Location Not Found
        </p>
      </div>
    );

  return (
    <div className="container">
      <Heading title="Locations Information" id={location?.id}>
        {edit ? (
          <Fragment>
            <MdClose onClick={() => setEdit(false)} className="text-2xl icon" />
            <MdCheck onClick={editHandler} className="text-2xl icon" />
          </Fragment>
        ) : (
          <Fragment>
            <MdEdit onClick={() => setEdit(true)} className="text-2xl icon" />
            <AuditLogs table="location" id={id} />
            <MdRefresh className="text-2xl icon " onClick={fetchLocation} />
          </Fragment>
        )}
      </Heading>

      <div className="grid lg:flex lg:space-x-8">
        <ImageSelector image={location.image} edit={edit} loading={loading} />
        <div className="items-start w-full info lg:grid-cols-3">
          <DetailPair
            label="Name"
            value={location.name}
            edit={edit}
            inputOptions={{
              disabled: true,
              value: toTitleCase(location.name),
            }}
            loading={loading}
          />
          <DetailPair
            label="Address"
            value={location.address}
            edit={edit}
            inputOptions={{
              disabled: true,
              value: toTitleCase(location.address),
            }}
            loading={loading}
          />
          <DetailPair
            label="Phone"
            value={location.phone}
            edit={edit}
            inputOptions={{
              type: "text",
              id: "phone",
              defaultValue: location.phone,
            }}
            loading={loading}
          />
          <DetailPair
            label="Tax Rate (%)"
            value={(location.tax * 100).toFixed(2)}
            edit={edit}
            inputOptions={{
              type: "number",
              id: "tax",
              step: "0.01",
              min: "0",
              defaultValue: (location.tax * 100).toFixed(2),
            }}
            loading={loading}
          />
          <DetailPair
            label="Delivery Rate ($)"
            value={location.deliveryRate}
            edit={edit}
            inputOptions={{
              type: "number",
              id: "deliveryRate",
              defaultValue: location.deliveryRate,
            }}
            loading={loading}
          />
          <LinkPair
            label="Google Maps Link"
            edit={edit}
            link={location?.googleMapsLink}
            inputOptions={{
              type: "text",
              id: "googleMapsLink",
              defaultValue: location.googleMapsLink,
            }}
            loading={loading}
          />
          <LinkPair
            label="Google-Iframe"
            edit={edit}
            link={location?.googleIframe}
            inputOptions={{
              type: "text",
              id: "googleIframe",
              defaultValue: location.googleIframe,
            }}
            loading={loading}
          />
          <LinkPair
            label="Apple Maps Link"
            edit={edit}
            link={location?.appleMapsLink}
            inputOptions={{
              type: "text",
              id: "appleMapsLink",
              defaultValue: location.appleMapsLink,
            }}
            loading={loading}
          />
          <SelectPair
            label="Active"
            value={location.active ? "Yes" : "No"}
            options={["Yes", "No"]}
            edit={edit}
            inputOptions={{
              id: "active",
              defaultValue: location.active ? "Yes" : "No",
            }}
            loading={loading}
          />
        </div>
      </div>

      {uploading && (
        <p className="text-stone-200">
          Uploading {uploading[0]}... {uploading[1]}%
        </p>
      )}
    </div>
  );
}
